// Vue
import { Component, Watch } from "vue-property-decorator";
import { Observer } from "mobx-vue";
// Template
import template from "./template.vue";
// Base Module
import ModuleBase from "#/common/ModuleBase";
// Components
import ModuleWrapper from "#/components/module_wrapper/ModuleWrapper";

import { Api } from "#/api";
import { TableColumnOptions } from "@mgs/ci2019-common/src/interfaces/TableColumnOptions";
import {
  ResultData,
  SpecialEquipmentData,
  TableResult,
} from "#/common/models/module/SpecialEquipmentData";

@Observer
@Component({
  mixins: [template],
  components: {
    ModuleWrapper,
  },
})
export class PowerTakeOffDrawingModule extends ModuleBase {
  public getBackgroundColorFromInstance(instance: object) {
    return "white";
  }

  private data: SpecialEquipmentData | null = null;
  private filter: object = {};
  private session: any = {};
  private isLoading: boolean = false;
  private isDownloading: boolean = false;
  private clear_select = "clear_select";

  @Watch("filter", { deep: true })
  onFilterChange() {
    Object.entries(this.filter).forEach(([key, value]) => {
      if (value === this.clear_select) {
        delete this.filter[key];
      }
    });
    this.loadData();
  }

  get selectFields() {
    if (this.data) {
      let merged = this.instance["filter"].map((item) => {
        if (this.data?.filterData[item.category_key].selected !== "") {
          this.data!.filterData[item.category_key].options[this.clear_select] =
            {
              code: this.clear_select,
              description: this.$t("PTO_DRAWINGS_TABLE_CLEAR_SELECT"),
            };
        }
        return { ...item, ...this.data?.filterData[item.category_key] };
      });
      merged = merged.filter((item) => item.options !== undefined);
      return merged;
    } else {
      return [];
    }
  }

  get vehicleData() {
    if (
      !this.data ||
      !Array.isArray(this.data.resultData) ||
      this.data.resultData.length !== 1 //PTO only should have 1 result
    ) {
      return [];
    }

    let result: any = [];
    //iterate through vehicle config and find corresponding entries in the result data
    this.instance["section_result"]["vehicle_config"].forEach((conf: any) => {
      let value = this.findValue(conf, true);
      if (value !== "") {
        result.push({
          description: conf.description,
          value: value,
        });
      }
    });
    return result;
  }

  findValue(config: any, asText: boolean): any {
    if (!this.data?.resultData[0]) {
      return "";
    }
    if (config.typ === "model") {
      return this.data?.resultData[0].model;
    }
    if (
      !Array.isArray(this.data?.resultData[0].data) ||
      this.data?.resultData[0].data.length === 0
    ) {
      return "";
    }
    for (let value of this.data?.resultData[0].data) {
      if (value.id == config.id && value.type === config.typ) {
        if (asText) {
          let result = value.value;
          if (value.description) {
            result += ": " + value.description;
          }
          //Don't return props here
          return result;
        } else {
          return value;
        }
      }
    }
    return "";
  }

  get tableColumns() {
    let result: { [key: string]: TableColumnOptions } = {};

    //Col 0 for Names
    result[0] = { name: "" };

    if (
      !this.data ||
      !Array.isArray(this.data.resultData) ||
      this.data.resultData.length !== 1 || //PTO only should have 1 result
      !this.data?.resultData[0] ||
      !this.data?.resultData[0].data
    ) {
      return result;
    }

    //Nebenantrieb Getriebeverbunden
    let i = 1;
    this.instance["section_result"]["result"].forEach((conf: any) => {
      let value = this.findValue(conf, true);
      if (value !== "") {
        result[i] = {
          name: value,
        };
        i++;
      }
    });

    return result;
  }

  get tableData() {
    if (
      !this.data ||
      !Array.isArray(this.data.resultData) ||
      this.data.resultData.length !== 1 || //PTO only should have 1 result
      !this.data?.resultData[0] ||
      !this.data?.resultData[0].data
    ) {
      return [];
    }

    //Checken ob es NA Daten gibt
    let hasData = false;
    for (const c of this.instance["section_result"]["result"]) {
      let value = this.findValue(c, true);
      if (value !== "") {
        hasData = true;
        break;
      }
    }
    if (!hasData) {
      return [];
    }

    let result: any[] = [];

    for (const k of this.instance["section_result"]["rows"]) {
      if (!k.description || !k.properties) {
        continue;
      }
      let rr: any = [];
      rr[0] = k.description;
      let r = 1;
      let hasValue = false;
      this.instance["section_result"]["result"].forEach((conf: any) => {
        let value = this.findValue(conf, false);
        if (value !== "" && value.properties) {
          rr[r] = [];
          let pr = 0;
          for (const p of k.properties) {
            if (p.id && value.properties[p.id]) {
              rr[r][pr] = value.properties[p.id];
              pr++;
              hasValue = true;
            }
          }
          r++;
        }
      });
      //Add only if we have at least one value
      if (hasValue) {
        result.push(rr);
      }
    }
    return result;
  }

  public created() {
    this.loadData();
  }

  getFinalConfig(): any {
    return this.instance["section_result"]["vehicle_config"].concat(
      this.instance["section_result"]["result"]
    );
  }

  public async loadData() {
    const urlParams = new URLSearchParams(window.location.search);
    let token = urlParams.get("token");

    if (token && !this.isLoading) {
      this.isLoading = true;

      //Load Session data for later calls
      this.session = await Api.katalogService.getSession(token);

      let filterGroups = this.instance["filter"].map(
        (filter: any) => filter.category_key
      );

      this.data = await Api.katalogService.getSpecialEquipment(
        filterGroups,
        Object.keys(this.filter).length > 0 ? this.filter : undefined,
        this.getFinalConfig(),
        token
      );
      this.isLoading = false;
    }
  }

  getSelectLabel(option: any) {
    if (option.code == this.clear_select || option.code == "") {
      return option.description;
    }
    return option.code + ": " + option.description;
  }
  getPlaceholderForSelectfield(field: { options: object }) {
    if (Object.values(field.options).length === 1) {
      let option = Object.values(field.options)[0];
      if (option.code == this.clear_select || option.code == "") {
        return option.description;
      }
      return option.code + ": " + option.description;
    } else {
      return undefined;
    }
  }

  onResetFilter(key) {
    if (this.filter[key]) {
      delete this.filter[key];
      this.data = null;
      this.onFilterChange();
    }
  }

  async onDownloadClick(type: string) {
    this.isDownloading = true;
    await Api.katalogService.downloadPtoDrawing(
      this.data?.resultData[0]?.model ?? "",
      this.data?.resultData[0]?.code ?? [],
      type,
      this.session.config.deliveryDate
    );
    this.isDownloading = false;
  }
}
